import React from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme } from 'react-jss';

import safeRest from './safeRest';

export const spacingOptions = Object.freeze([1, 2, 3, 4, 5, 6, 7, 8, 9]);

// this pattern is described in React's documentation: https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging
function getDisplayName(Component) {
  return Component.displayName || Component.name || 'Component';
}

function withSpacing(Component) {
  /**
   * JSS injects css styles in the order of `createUseStyles` usage.
   * Hence, useStyles needs to be instantiated every single time while
   * using this HOC because it should be the last `createUseStyles` usage
   * for a component. With this mechanism, `withSpacing` overwrites the component
   * spacing. Not ideal for a design system, but we are waiting on the
   * below ticket.
   *
   * Note: https://jira.surveymonkey.com/browse/WRENCH-1107
   *
   * If the decision is have `withStyles` NOT overwrite the component spacing,
   * just move this declaration outside of this component. `withSpacing` will then
   * add to the styles and not overwrite them.
   */
  const useStyles = createUseStyles((theme) => ({
    wdsSpacing: ({ margin, padding }) => {
      return {
        marginTop: theme.spacing[margin.t],
        marginRight: theme.spacing[margin.r],
        marginBottom: theme.spacing[margin.b],
        marginLeft: theme.spacing[margin.l],
        paddingTop: theme.spacing[padding.t],
        paddingRight: theme.spacing[padding.r],
        paddingBottom: theme.spacing[padding.b],
        paddingLeft: theme.spacing[padding.l],
      };
    },
  }));

  function WithSpacing({
    m,
    mx,
    my,
    mt,
    mb,
    ml,
    mr,
    p,
    px,
    py,
    pt,
    pb,
    pl,
    pr,
    ...rest
  }) {
    const margin = {
      t: m || my || mt,
      b: m || my || mb,
      l: m || mx || ml,
      r: m || mx || mr,
    };

    const padding = {
      t: p || py || pt,
      b: p || py || pb,
      l: p || px || pl,
      r: p || px || pr,
    };

    const theme = useTheme();
    const classes = useStyles({
      margin,
      padding,
      theme,
    });

    return <Component className={classes.wdsSpacing} {...safeRest(rest)} />;
  }

  WithSpacing.defaultProps = {
    m: undefined,
    mx: undefined,
    my: undefined,
    mt: undefined,
    mb: undefined,
    ml: undefined,
    mr: undefined,
    p: undefined,
    px: undefined,
    py: undefined,
    pt: undefined,
    pb: undefined,
    pl: undefined,
    pr: undefined,
  };

  WithSpacing.propTypes = {
    m: PropTypes.oneOf(spacingOptions),
    mx: PropTypes.oneOf(spacingOptions),
    my: PropTypes.oneOf(spacingOptions),
    mt: PropTypes.oneOf(spacingOptions),
    mb: PropTypes.oneOf(spacingOptions),
    ml: PropTypes.oneOf(spacingOptions),
    mr: PropTypes.oneOf(spacingOptions),
    p: PropTypes.oneOf(spacingOptions),
    px: PropTypes.oneOf(spacingOptions),
    py: PropTypes.oneOf(spacingOptions),
    pt: PropTypes.oneOf(spacingOptions),
    pb: PropTypes.oneOf(spacingOptions),
    pl: PropTypes.oneOf(spacingOptions),
    pr: PropTypes.oneOf(spacingOptions),
  };
  WithSpacing.displayName = `WithSpacing(${getDisplayName(Component)})`;
  WithSpacing.getOriginalComponent = () => Component;
  return WithSpacing;
}

export default withSpacing;
